/**
 * imageIngest - shrink user photos at the moment they enter an
 * editor, so design snapshots stop carrying multi-megabyte data
 * URLs (which blow the localStorage quota, fail saves, and are
 * copied into every undo snapshot).
 *
 * A photo field displays at ~110 CSS px wide; 900 px on the long
 * edge is generous headroom for the 300-DPI document export while
 * keeping a JPEG re-encode in the ~100-250 KB range.
 */

export const INGEST_MAX_DIMENSION = 900;
export const INGEST_JPEG_QUALITY = 0.85;

/** Pure sizing math (unit-testable): fit w×h inside maxDim, never upscale. */
export function fitWithin(w: number, h: number, maxDim: number): { width: number; height: number } {
    if (!w || !h || (w <= maxDim && h <= maxDim)) return { width: w, height: h };
    const scale = maxDim / Math.max(w, h);
    return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

function readAsDataUrl(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error || new Error('Could not read the file'));
        r.readAsDataURL(file);
    });
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        // Some environments never fire load or error for a data URL;
        // the timeout keeps the fallback path reachable everywhere.
        const timer = setTimeout(() => reject(new Error('Image load timed out')), 2500);
        img.onload = () => { clearTimeout(timer); resolve(img); };
        img.onerror = () => { clearTimeout(timer); reject(new Error('Could not decode the image')); };
        img.src = src;
    });
}

/**
 * Read an image file and return a data URL downscaled to
 * INGEST_MAX_DIMENSION (JPEG re-encode). Falls back to the raw
 * data URL when the image cannot be decoded or the canvas is
 * unavailable, so an upload never hard-fails on the shrink step.
 */
export async function downscaleImageFile(file: File, maxDim: number = INGEST_MAX_DIMENSION): Promise<string> {
    const raw = await readAsDataUrl(file);
    try {
        const img = await loadImage(raw);
        const { width, height } = fitWithin(img.naturalWidth || img.width, img.naturalHeight || img.height, maxDim);
        if (width === (img.naturalWidth || img.width) && height === (img.naturalHeight || img.height) && raw.length < 400_000) {
            return raw; // already small: keep the original bytes/format
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return raw;
        ctx.drawImage(img, 0, 0, width, height);
        return canvas.toDataURL('image/jpeg', INGEST_JPEG_QUALITY);
    } catch {
        return raw;
    }
}
